/**
 * KND-2 — the pure half of the termination block.
 *
 * No request, no React, no clock. Everything here turns the server's
 * `TenancyTerminationResponse` into what the block shows, or the form into the
 * PUT body — so the rules can be tested without rendering anything.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO (discovery E5): no notice-period
 * arithmetic, no "fristgerecht", no month-end rule, no legal rating of any
 * kind. The dates are what the admin enters; the server applies the existing
 * lifecycle / cap / overlap rules and answers with the result.
 */
import {
  businessLocalToIso,
  isoToBusinessLocalInput,
} from "../../../../utils/datetime";

/** The Swiss wall-clock time of the server's default latest check-out (E7). */
export const DEFAULT_CHECKOUT_WALL_TIME = "10:00";

/** Mirrors the request schema's limits, so the input stops where the server would. */
export const TERMINATION_REASON_MAX_LENGTH = 1000;
export const HANDOVER_LOCATION_MAX_LENGTH = 500;
export const NOTE_MAX_LENGTH = 2000;

const TERMINATED_BY_LABELS = {
  tenant: "Mieter",
  landlord: "Vermieter / Verwaltung",
  other: "Sonstiges",
};

const partyOf = (response) =>
  String(response?.terminated_by || "").trim().toLowerCase();

/** "Mieter" / "Vermieter / Verwaltung" / "Sonstiges" / "nicht erfasst". */
export function terminatedByLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "nicht erfasst";
  return TERMINATED_BY_LABELS[key] || key;
}

/** True for a tenancy the Storno path has closed. */
export function isCancelledTenancy(tn) {
  const status = String(tn?.status || "").toLowerCase();
  const display = String(tn?.display_status || "").toLowerCase();
  return status === "cancelled" || display === "cancelled";
}

/** True for a tenancy that has not begun yet (E1: may still get a termination). */
export function isNotYetStartedTenancy(tn) {
  const status = String(tn?.status || "").toLowerCase();
  const display = String(tn?.display_status || "").toLowerCase();
  return display === "reserved" || (!display && status === "reserved");
}

/**
 * What the block renders, from the server's `state` plus the tenancy.
 *
 * `legacy` splits in three, and only ONE of them can be taken over:
 *
 * - `legacy_tenant` — the party is already recorded as the tenant. The
 *   termination may be taken over into this workflow, on purpose only.
 * - `legacy_other` — landlord or another party. The server refuses a PUT or
 *   withdraw with 409 (KND-1 hardening B); read-only here.
 * - `legacy_unknown` — no party recorded (missing, empty, or a value this
 *   client does not know). FAIL CLOSED, read-only. V1 is the tenant's
 *   ordinary termination only (E6), and a PUT always writes
 *   `terminated_by = tenant`: offering the takeover here would turn an
 *   undocumented party into the tenant by inference. The server would accept
 *   that PUT, so the refusal has to live in this block.
 */
export function terminationViewState(response) {
  const state = response?.state;
  if (state === "recorded") return "recorded";
  if (state === "withdrawn") return "withdrawn";
  if (state === "legacy") {
    const party = partyOf(response);
    if (party === "tenant") return "legacy_tenant";
    if (party === "landlord" || party === "other") return "legacy_other";
    return "legacy_unknown";
  }
  if (state === "none") return "none";
  return "unknown";
}

/**
 * The notice period to show: the snapshot frozen with the termination, else
 * the tenancy's own value, else nothing. Neutral wording only.
 */
export function noticePeriodMonths(record, tn) {
  const snapshot = record?.notice_period_months_snapshot;
  if (snapshot != null) return snapshot;
  const canonical = tn?.notice_period_months;
  return canonical != null ? canonical : null;
}

export function noticePeriodText(months) {
  if (months == null || months === "") return "—";
  const n = Number(months);
  if (!Number.isFinite(n)) return "—";
  return n === 1 ? "1 Monat" : `${n} Monate`;
}

/**
 * True when the stored check-out is exactly the server default for this
 * termination date — the date at 10:00 in Swiss wall-clock time. Read with
 * the shared business-time helper, never with the device clock (Regel 51).
 */
export function isDefaultCheckout(latestCheckoutIso, terminationDate) {
  if (!latestCheckoutIso || !terminationDate) return false;
  return (
    isoToBusinessLocalInput(latestCheckoutIso) ===
    `${terminationDate}T${DEFAULT_CHECKOUT_WALL_TIME}`
  );
}

export const EMPTY_TERMINATION_FORM = Object.freeze({
  noticeGivenAt: "",
  terminationEffectiveDate: "",
  reason: "",
  handoverLocal: "",
  checkoutLocal: "",
  handoverLocation: "",
  note: "",
});

/**
 * Seed the form.
 *
 * `recorded` edits the stored record. `legacy_tenant` pre-fills the two dates
 * the tenancy already carries and nothing else — the record does not exist
 * yet. No other legacy state ever opens a form (see terminationViewState).
 * Everything else (`none`, `withdrawn`) is a NEW termination and starts blank:
 * a withdrawn act is history, not a draft.
 *
 * The check-out field holds an OVERRIDE. When the stored value is the default
 * for the stored date it stays empty, so a changed termination date gets the
 * new default from the server instead of keeping the old day.
 */
export function formFromResponse(response) {
  const view = terminationViewState(response);
  if (view === "recorded") {
    const record = response?.termination || {};
    const te = response?.termination_effective_date || "";
    return {
      noticeGivenAt: response?.notice_given_at || "",
      terminationEffectiveDate: te,
      reason: record.termination_reason || "",
      handoverLocal: isoToBusinessLocalInput(record.handover_at),
      checkoutLocal: isDefaultCheckout(record.latest_checkout_at, te)
        ? ""
        : isoToBusinessLocalInput(record.latest_checkout_at),
      handoverLocation: record.handover_location || "",
      note: record.note || "",
    };
  }
  if (view === "legacy_tenant") {
    return {
      ...EMPTY_TERMINATION_FORM,
      noticeGivenAt: response?.notice_given_at || "",
      terminationEffectiveDate: response?.termination_effective_date || "",
    };
  }
  return { ...EMPTY_TERMINATION_FORM };
}

/** Field errors for the two required dates, in German. Empty object = valid. */
export function validateTerminationForm(form) {
  const errors = {};
  if (!form?.noticeGivenAt) {
    errors.noticeGivenAt = "Bitte das Eingangsdatum der Kündigung angeben.";
  }
  if (!form?.terminationEffectiveDate) {
    errors.terminationEffectiveDate =
      "Bitte das Ende des Mietverhältnisses angeben.";
  }
  return errors;
}

const trimmed = (value) => {
  const s = String(value ?? "").trim();
  return s || null;
};

/**
 * The PUT body. Only what the admin decides — never `terminated_by`, never
 * `actual_move_out_date`, never `cancellation_reason` (the Storno domain).
 * Optional fields left empty are omitted: the PUT is the full representation,
 * so an omitted field is stored as empty, and an omitted check-out is the
 * server's default.
 */
export function buildTerminationPayload(form) {
  const payload = {
    notice_given_at: form.noticeGivenAt,
    termination_effective_date: form.terminationEffectiveDate,
  };
  const reason = trimmed(form.reason);
  if (reason) payload.termination_reason = reason;
  const handoverAt = businessLocalToIso(form.handoverLocal);
  if (handoverAt) payload.handover_at = handoverAt;
  const latestCheckoutAt = businessLocalToIso(form.checkoutLocal);
  if (latestCheckoutAt) payload.latest_checkout_at = latestCheckoutAt;
  const location = trimmed(form.handoverLocation);
  if (location) payload.handover_location = location;
  const note = trimmed(form.note);
  if (note) payload.note = note;
  return payload;
}
